import { useCallback, useMemo, useState } from 'react';

import { PackageOpen } from 'lucide-react';

import { Button } from '@/_components';
import { CardContainer, SectionHeading } from '@/_containers';
import { showFormField } from '@/_shared';

import { AddPickTicketsItems } from './create-pick-tickets-item-modal';
import { InventoryKitItemsTable } from './pick-tickets-items-table';

import { usePickTicketsFormContext } from '../../form-context';
import type { PickTicketsItemFormSchema } from '../../pick-ticket-items-schemas';

export function InventoryKitItemsSection({ isEditable }: { isEditable: boolean }) {
  const form = usePickTicketsFormContext();
  const currentItems = useMemo(() => form.watch('pick_ticket_items') || [], [form]);
  const pickTicketId = form.watch('id');
  const [open, setOpen] = useState(false);

  const addedKitItems = useMemo(
    () => currentItems.map((item) => item.inventory?.id).filter((id) => typeof id === 'number'),
    [currentItems]
  );

  const handleDelete = useCallback(
    (id: number) => {
      const newItems = currentItems.filter((item) => item.id !== id);
      form.setValue('pick_ticket_items', newItems, {
        shouldDirty: true,
      });
    },
    [form, currentItems]
  );

  const handleDataUpdate = useCallback(
    (data: PickTicketsItemFormSchema[]) => {
      form.setValue('pick_ticket_items', data, {
        shouldDirty: true,
      });
    },
    [form]
  );

  const handleInventoryKitItemCreate = useCallback(
    (nItems: PickTicketsItemFormSchema[]) => {
      const newItems = [...currentItems];
      nItems.forEach((item) => {
        newItems.push(item);
      });

      form.setValue('pick_ticket_items', newItems, {
        shouldDirty: true,
      });
      setOpen(false);
    },
    [form, currentItems]
  );

  if (!showFormField(isEditable, currentItems.length)) {
    return null;
  }

  return (
    <>
      <CardContainer>
        <SectionHeading
          text="Pick Ticket Items"
          icon={PackageOpen}
          rightSlot={
            isEditable ? (
              <Button size="xs" onClick={() => setOpen(true)}>
                Add Pick Ticket Item
              </Button>
            ) : undefined
          }
        />

        <InventoryKitItemsTable
          tableData={currentItems}
          isEditable={isEditable}
          cacheBuster={currentItems.length}
          handleDelete={handleDelete}
          handleDataUpdate={handleDataUpdate}
        />
      </CardContainer>

      <AddPickTicketsItems
        open={open}
        onClose={() => setOpen(false)}
        title="Add Pick Ticket Item"
        itemCount={currentItems.length}
        pickTicketId={pickTicketId}
        addedKitItems={addedKitItems}
        handleInventoryKitItemCreate={handleInventoryKitItemCreate}
      />
    </>
  );
}
